/** The four palettes DESIGN.md §9 names, and only those. */
export const ART_VARIANTS = ["dusk", "ember", "sky", "dawn"] as const;
export type ArtVariant = (typeof ART_VARIANTS)[number];

export interface ArtPalette {
  readonly shape: "square" | "dot";
  readonly bg: string;
  readonly speckle: string;
  readonly tip: string;
  /** Ten steps, from the field's edge to its bright core. */
  readonly ramp: readonly string[];
  /** The woven texture over square cells: a faint grid in ink, fine dots in white. */
  readonly ink: string;
  readonly white: string;
}

const RAMP_STEPS = 10;

/**
 * A palette, read off the page's design tokens (`--art-<variant>-*`).
 *
 * A canvas cannot take a class, so this is how its colours stay in DESIGN.md
 * rather than in a component. A token that is missing is a stylesheet that did
 * not load or a variant the design never named — said, not drawn in black.
 */
export function readPalette(element: Element, variant: ArtVariant): ArtPalette {
  const style = getComputedStyle(element);
  const read = (property: string): string => {
    const value = style.getPropertyValue(property).trim();
    if (value === "") throw new Error(`${property} is not defined; run pnpm tokens`);
    return value;
  };
  const token = (name: string): string => read(`--art-${variant}-${name}`);
  const shape = token("shape");
  return {
    shape: shape === "dot" ? "dot" : "square",
    bg: token("bg"),
    speckle: token("speckle"),
    tip: token("tip"),
    ramp: Array.from({ length: RAMP_STEPS }, (_, step) => token(String(step))),
    ink: read("--color-ink"),
    white: read("--color-white"),
  };
}
